import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Sparkles, Loader2, Copy as CopyIcon, Mail } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Phase E polish — AR-aging "Draft 3 reminder variants" dialog.
 *
 * Calls /api/ai-writer/payment-reminder-variants to get Gentle / Firm / Final
 * email bodies, lets the Office Manager pick one, edit, and either copy it to
 * the clipboard or push directly into the existing AR `send-reminders` flow.
 *
 * Props:
 *   open / onOpenChange
 *   receivable: { id, invoice_number, client_name, amount, days_overdue }
 *   onSend?(receivable, customMessage) — called when user clicks "Send via Reminder Email"
 */
export default function AiReminderVariantsDialog({ open, onOpenChange, receivable, onSend }) {
  const [busy, setBusy] = useState(false);
  const [variants, setVariants] = useState([]);
  const [selectedIdx, setSelectedIdx] = useState(0);
  const [edited, setEdited] = useState("");

  const generate = async () => {
    if (!receivable) return;
    setBusy(true);
    try {
      const res = await axios.post(`${API}/ai-writer/payment-reminder-variants`, {
        client_name: receivable.client_name || "",
        invoice_number: receivable.invoice_number || "",
        amount_due: Number(receivable.amount || 0),
        days_overdue: Number(receivable.days_overdue || 0),
      });
      const vs = res.data.variants || [];
      setVariants(vs);
      setSelectedIdx(0);
      setEdited(vs[0]?.body || "");
      toast.success("3 variants drafted");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI generation failed");
    } finally {
      setBusy(false);
    }
  };

  // Reset on open
  React.useEffect(() => {
    if (open) {
      setVariants([]);
      setSelectedIdx(0);
      setEdited("");
    }
  }, [open]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(edited);
      toast.success("Copied to clipboard");
    } catch (e) {
      toast.error("Copy failed");
    }
  };

  const send = () => {
    onSend?.(receivable, edited);
    onOpenChange?.(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="ai-reminder-variants-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Sparkles className="h-4 w-4 text-emerald-600" /> AI Reminder Variants</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          {receivable && (
            <p className="text-xs text-muted-foreground">For <strong>{receivable.client_name}</strong> · Invoice <strong>{receivable.invoice_number}</strong> · ${Number(receivable.amount || 0).toFixed(2)} · {receivable.days_overdue || 0} days overdue</p>
          )}
          {variants.length === 0 ? (
            <Button onClick={generate} disabled={busy} className="w-full bg-emerald-600 hover:bg-emerald-700" data-testid="generate-variants-btn">
              {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Sparkles className="h-4 w-4 mr-1" />}
              Draft 3 variants (Gentle · Firm · Final)
            </Button>
          ) : (
            <>
              <div className="flex gap-1 flex-wrap" data-testid="variant-tabs">
                {variants.map((v, idx) => (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => { setSelectedIdx(idx); setEdited(v.body || ""); }}
                    className={`px-3 py-1.5 text-xs rounded-md border transition ${selectedIdx === idx ? "bg-emerald-100 border-emerald-500 text-emerald-700" : "bg-muted/30 hover:bg-muted"}`}
                    data-testid={`variant-tab-${idx}`}
                  >
                    {v.label}
                  </button>
                ))}
              </div>
              <Textarea value={edited} onChange={(e) => setEdited(e.target.value)} rows={10} className="text-sm" data-testid="variant-edit-textarea" />
              <p className="text-xs text-muted-foreground">Edit freely — the variants are just starting points.</p>
            </>
          )}
        </div>
        <DialogFooter className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={() => onOpenChange?.(false)}>Close</Button>
          {variants.length > 0 && (
            <>
              <Button variant="outline" onClick={copy} data-testid="copy-variant-btn"><CopyIcon className="h-4 w-4 mr-1" /> Copy</Button>
              <Button variant="outline" onClick={generate} disabled={busy} data-testid="regenerate-variants-btn">
                {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Sparkles className="h-4 w-4 mr-1" />}
                Regenerate
              </Button>
              {onSend && (
                <Button onClick={send} className="bg-emerald-600 hover:bg-emerald-700" data-testid="send-variant-btn"><Mail className="h-4 w-4 mr-1" /> Send</Button>
              )}
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
